import type { ReactNode } from "react";
import {
  CheckIcon,
  ChildrenIcon,
  GridIcon,
  InfoIcon,
  PlayIcon,
  PointIcon,
  PrivacyIcon,
  ReportsIcon,
  SchoolIcon,
  StickerIcon,
  TargetIcon,
  TrendUpIcon,
  VoiceIcon,
} from "../../shared/ui/icons";
import type { Lang, Localized } from "../../shared/i18n";
import type { Role } from "../../shared/types/prisma-enums";
import {
  OPERATOR_ROLE_COPY,
  type OperatorRole,
  type Permission,
} from "../../shared/access/permissions";
import type { RouteId } from "./routes";
import {
  IconActivity,
  IconAnalytics,
  IconAttendance,
  IconAward,
  IconBroadcast,
  IconChildren,
  IconClasses,
  IconCoupon,
  IconDevice,
  IconGames,
  IconGroups,
  IconHelp,
  IconInbox,
  IconOverview,
  IconParent,
  IconPeople,
  IconPlay,
  IconQuote,
  IconRenew,
  IconReports,
  IconRevenue,
  IconSchool,
  IconSettings,
  IconSupport,
  IconTeacher,
  IconTeam,
  IconWebsite,
} from "../../shared/console/icons";

/**
 * What the sidebar says, and what it draws beside it.
 *
 * 🔴 THIS FILE EXISTS BECAUSE THE NAVIGATION WAS THE ONE UNTRANSLATED SURFACE IN
 * THE PRODUCT, AND `AppRouter` SAID SO IN WRITING.
 *
 * *"`RouteDef.title` is a single string, so `navFor` returned English labels
 * while every screen behind those links was bilingual — the nav was the one
 * untranslated surface in the product."* ✅ **Moot since ADR-044:** the product
 * is English only, so a single-string title is now the correct shape rather
 * than a gap. This table stays because the LABELS still differ from the route
 * titles, which is what it was really for.
 *
 * ⚠️ SO IT IS FIXED HERE RATHER THAN THERE, AND THAT IS THE WHOLE DESIGN.
 * `routes.ts` is the routing table — paths, roles, fallbacks — and its `title`
 * stays a plain string that a test can assert and a developer can read. What a
 * human is shown is a presentation concern, and it belongs next to the icon that
 * is shown with it rather than inside the table that decides who may navigate.
 *
 * 🔴 EVERY ICON IS FROM THE GENERATED 20-ICON SET (`shared/ui/icons.tsx`). §6:
 * one stroke set, shared by both shells — and *"emoji are not icons"*.
 */

export interface NavPresentation {
  label: Localized<string>;
  /**
   * What the CHROME BAR calls this screen, when the sidebar's word would read
   * wrongly at the top of the page.
   *
   * 🔴 IT EXISTS FOR EXACTLY ONE CASE AND SHOULD STAY RARE: a tier's home. The
   * sidebar item is a row in a list of nouns, so "School" reads correctly there
   * — it is the school's own overview, sitting above Reports and Children. The
   * same word alone above the page reads as a category rather than a place, and
   * the platform tier already calls its equivalent row "Dashboard".
   *
   * ⚠️ EVERY OTHER ROUTE MUST LEAVE THIS UNSET, and `pageTitleFor` then falls
   * through to `label`. Two words for one screen is precisely the drift this
   * file was written to stop — an override per route would recreate it with a
   * second column instead of a second file.
   */
  pageTitle?: Localized<string>;
  icon: ReactNode;
  /**
   * The colour this item is known by — a token, always.
   *
   * 🔴 IDENTITY, NOT STATUS, WHICH IS THE SAME DISTINCTION `StatCard` MAKES AND
   * THE ONLY GROUNDS ON WHICH COLOUR IS ALLOWED HERE. §12 requires status to be
   * *"icon + colour + text, always all three"*, so a nav item whose colour meant
   * anything would be asserting a state it does not know. This makes "Attendance"
   * findable in the same place, in the same colour, every morning — which is what
   * a person navigating by shape rather than by reading actually uses.
   *
   * ⚠️ IT IS NEVER THE ONLY SIGNAL. The current item is a filled magenta pill
   * with `aria-current="page"`; the tint is on the resting state, where nothing
   * is being communicated except which row is which.
   *
   * ⚠️ AND IT IS DRAWN FROM §3's FIXED MAP WHERE ONE EXISTS. Progress carries the
   * brand, Attendance the leaf it uses on the register, Awards the sunshine of a
   * coin — a second palette invented for the sidebar is how two screens end up
   * calling the same thing by two colours.
   */
  tint: string;
}

/**
 * ⚠️ PARTIAL, AND DELIBERATELY. Only routes that appear in `navFor` need an
 * entry — the settings screens are reached from the sidebar footer and the auth
 * screens from nowhere. `navLabel` falls back to `RouteDef.title`, so a route
 * added to the table without a line here degrades to English rather than to a
 * blank sidebar.
 */
const NAV: Partial<Record<RouteId, NavPresentation>> = {
  /* ── the teacher and parent tiers ── */
  teacher: {
    label: { en: "My classes" },
    icon: <ChildrenIcon size={18} />,
    tint: "var(--color-primary)",
  },
  parent: {
    label: { en: "Overview" },
    icon: <TrendUpIcon size={18} />,
    tint: "var(--color-primary)",
  },
  /*
   * ⚠️ SIX ITEMS IS THE MOST THIS TIER MAY HAVE, AND IT NOW HAS SIX. The budget
   * is spent: Overview, Attendance, Awards, Notices, Messages, Privacy. A
   * seventh needs one of these to leave, and the
   * labels are one word each for that reason: the parent tier is read on a
   * phone, where these are the thumb bar across the bottom (`nav="adaptive"` in
   * `DashboardShell`). Anything longer wraps or truncates at 360 px, which is
   * the width the design system sizes against — the same words sit in the left
   * sidebar from `md` up, where they have room to spare, so the phone is the
   * constraint.
   */
  "parent-attendance": {
    label: { en: "Attendance" },
    icon: <CheckIcon size={18} />,
    tint: "var(--color-leaf)",
  },
  "parent-achievements": {
    label: { en: "Awards" },
    icon: <StickerIcon size={18} />,
    tint: "var(--color-sunshine)",
  },
  /*
   * ✅ THE SIXTH ITEM, WHICH IS THE MOST THIS TIER MAY HAVE — see the note above.
   *
   * 🔴 "Notices", NOT "Announcements", AND THE SHORTER WORD IS THE WHOLE REASON
   * THE ITEM FITS. These labels are one word each because they are the thumb bar
   * across the bottom of a 360 px phone; "Announcements" is thirteen characters
   * and would truncate at six items. The screen's own heading says
   * "Announcements" in full, where there is room.
   *
   * ⚠️ IT IS DELIBERATELY NOT NEXT TO "Messages", THOUGH THEY LOOK ALIKE. C4 is
   * the record of what WE sent; this is what their SCHOOL said. Adjacent, a
   * parent would read them as two halves of one inbox and check the wrong one.
   */
  "parent-announcements": {
    label: { en: "Notices" },
    icon: <InfoIcon size={18} />,
    tint: "var(--color-coral)",
  },
  "parent-messages": {
    label: { en: "Messages" },
    icon: <VoiceIcon size={18} />,
    tint: "var(--color-sky)",
  },
  /*
   * ⚠️ THE SEVENTH, AND THE SIX-ITEM NOTE ABOVE IS NOW A NOTE ABOUT SEVEN.
   * Added on request. "Reports" is seven characters — the shortest true word for
   * it, and short enough not to truncate on the 360 px thumb bar the way
   * "Progress report" would; what it costs is the spare width the other six had,
   * not a wrap.
   *
   * 🔴 `ReportsIcon`, WHICH ALREADY EXISTS IN THE GENERATED SET. The school tier
   * uses the same glyph for the same idea, so the one word a parent and a head
   * teacher share is drawn the same way in both places.
   */
  "parent-reports": {
    label: { en: "Reports" },
    icon: <ReportsIcon size={18} />,
    tint: "var(--color-lilac)",
  },
  /* ── the school tier ── */
  school: {
    label: { en: "Home" },
    /* The tier's home — see `pageTitle`. "School" is a row in the sidebar's
       list; alone above the page it names a category, not a place. */
    pageTitle: { en: "Dashboard" },
    icon: <GridIcon size={18} />,
    tint: "var(--color-primary)",
  },
  "school-reports": {
    label: { en: "Reports" },
    icon: <ReportsIcon size={18} />,
    tint: "var(--color-sky)",
  },
  /*
   * E7 — every child in the school.
   *
   * ⚠️ "Children", NOT "Students", AND NOT "Pupils". The product calls them
   * children everywhere an adult reads — E1's tile, the roster import, the
   * parent tier — and a third noun for the same people on the one screen that
   * lists them is how a school ends up asking whether the two counts mean
   * different things.
   *
   * ⚠️ INDIGO, THE TINT THE SCHOOL COLUMN HAD FREE. Overview, Reports,
   * Teachers, Classes and Our plan spend primary, sky, grape, leaf and teal; a
   * repeat would make two unrelated rows read as a pair. Coral is free again
   * now that Broadcast is gone, but it stays the platform tier's — reusing it
   * one tier down is the pairing this note exists to avoid.
   */
  "school-students": {
    label: { en: "Children" },
    icon: <ChildrenIcon size={18} />,
    tint: "var(--color-indigo)",
  },
  "school-teachers": {
    label: { en: "Teachers" },
    icon: <PointIcon size={18} />,
    tint: "var(--color-grape)",
  },
  /* E4 — a class, its teacher, its subjects and its week. See `routes.ts` for
     why those four are one screen rather than four addresses. */
  "school-classes": {
    label: { en: "Classes" },
    icon: <SchoolIcon size={18} />,
    tint: "var(--color-leaf)",
  },
  /*
   * ⚠️ CORAL, WHICH THE SCHOOL COLUMN HAS FREE AGAIN. Overview, Reports,
   * Children, Teachers, Classes and Our plan spend primary, sky, indigo, leaf,
   * grape and teal; coral came back when the broadcast item was removed.
   */
  "school-games": {
    label: { en: "Games" },
    icon: <PlayIcon size={18} />,
    tint: "var(--color-coral)",
  },
  "school-subscription": {
    label: { en: "Billing" },
    icon: <ReportsIcon size={18} />,
    tint: "var(--color-teal)",
  },

  /*
   * ── the platform tier ──
   *
   * 🔴 TEN ITEMS, EIGHT IDENTITY COLOURS, SO TWO PAIRS SHARE ONE — AND
   * EACH PAIR IS A PAIR ON PURPOSE. The rule this file states is that *"a repeat
   * would make two unrelated rows read as a pair"*; the answer to a tier that
   * outgrew the palette is therefore to make the repeats RELATED rather than to
   * invent a ninth colour:
   *
   *   · **sky** — Schools and Requests. The same object one step apart: a
   *     request becomes a school, and they sit next to each other for that
   *     reason (`routes.ts`).
   *   · **grape** — Audit log and Operators. Who acted, and who may act.
   *   · **indigo** — System. The shelf an operator reaches for when something
   *     is wrong rather than when something is being sold.
   *
   * ⚠️ THE MONEY PAIR IS DELIBERATELY *NOT* PAIRED. Subscriptions and Coupons
   * are adjacent and are compared against one another daily — one colour across
   * both would be the one place the tint actively hides which is which.
   */
  platform: {
    label: { en: "Overview" },
    icon: <GridIcon size={18} />,
    tint: "var(--color-primary)",
  },
  "platform-schools": {
    label: { en: "Schools" },
    icon: <SchoolIcon size={18} />,
    tint: "var(--color-sky)",
  },
  "platform-requests": {
    label: { en: "Pipeline" },
    icon: <VoiceIcon size={16} />,
    tint: "var(--color-sky)",
  },
  /*
   * ⚠️ HERE THOUGH IT IS NOT IN THE SIDEBAR — the `settings-accessibility`
   * precedent two blocks down. `platform-children` is `chrome`, so `navFor`
   * skips it, but the page HEADING above it is drawn from this same table.
   */
  "platform-children": {
    label: { en: "Children" },
    icon: <ChildrenIcon size={18} />,
    tint: "var(--color-primary)",
  },
  "platform-parents": {
    label: { en: "Parents" },
    icon: <ChildrenIcon size={18} />,
    tint: "var(--color-sunshine)",
  },
  "platform-engagement": {
    label: { en: "Analytics" },
    icon: <TrendUpIcon size={18} />,
    tint: "var(--color-teal)",
  },
  "platform-subscriptions": {
    label: { en: "Revenue" },
    icon: <ReportsIcon size={18} />,
    tint: "var(--color-teal)",
  },
  "platform-coupons": {
    label: { en: "Coupons" },
    icon: <StickerIcon size={18} />,
    tint: "var(--color-coral)",
  },

  "platform-audit": {
    label: { en: "Activity log" },
    icon: <PrivacyIcon size={18} />,
    tint: "var(--color-grape)",
  },
  "platform-broadcast": {
    label: { en: "Broadcast" },
    icon: <VoiceIcon size={18} />,
    tint: "var(--color-coral)",
  },
  /*
   * ⚠️ "Operators", NOT "Team" OR "Users". Every other tier calls its people
   * teachers, admins or parents; this row is the six accounts that can reach
   * every child on the platform. A softer word for the most privileged list in
   * the product would be the only place it is not named plainly.
   */
  "platform-operators": {
    /* ✅ "Roles & Permissions" UNTIL THE PERMISSION MATRIX CAME OUT. Roles are a
       fixed enum in `user.prisma` and nothing in the product grants or revokes
       one, so the screen behind this item manages operator ACCOUNTS and always
       did — `routes.ts` has called it "Operators" all along, and the sidebar was
       the only place still promising otherwise. */
    label: { en: "Team" },
    icon: <PrivacyIcon size={18} />,
    tint: "var(--color-grape)",
  },
  "platform-system": {
    label: { en: "Settings" },
    icon: <TargetIcon size={18} />,
    tint: "var(--color-indigo)",
  },

  /*
   * ⚠️ G1–G4 ARE HERE THOUGH THEY ARE NOT IN THE SIDEBAR. `navFor` never returns
   * them — they carry no `roles` and are reached from the shell footer — but the
   * page HEADING above them is drawn from this same table, so a label here and
   * a heading there drifting apart is what this file exists to stop. The icon
   * goes unused for them, which is cheaper than a second map.
   */
  "settings-accessibility": {
    label: { en: "Accessibility" },
    icon: <InfoIcon size={18} />,
    tint: "var(--color-teal)",
  },
  "settings-camera": {
    label: { en: "Camera and privacy" },
    icon: <PrivacyIcon size={18} />,
    tint: "var(--color-grape)",
  },
};

/**
 * The glyph and tint on the shell's own Settings control.
 *
 * 🔴 IT USED TO BE `NAV['settings-account']`, AND THAT ROUTE NO LONGER EXISTS.
 * The control is drawn on every tier and opens a DIFFERENT screen on each
 * (`settingsHomeFor`), so it never took its icon from its own destination — it
 * borrowed G1's, deliberately, so the one shared piece of chrome looks the same
 * everywhere instead of changing glyph per tier. That intent outlives G1; what
 * had to change is where the glyph is written down.
 *
 * ⚠️ SAME PRIVACY GLYPH AND SAME INDIGO AS BEFORE, so no tier's chrome moves.
 */
/**
 * The console redesign's labels and icons (Figma "Kalqy", Components ›
 * Sidebar/*). Consulted before `NAV`, so a route keeps its old presentation
 * only where the redesign has none.
 */
const CONSOLE_NAV: Partial<Record<RouteId, { label: string; icon: ReactNode }>> = {
  platform: { label: "Overview", icon: <IconOverview /> },
  "platform-engagement": { label: "Analytics", icon: <IconAnalytics /> },
  "platform-requests": { label: "Pipeline", icon: <IconInbox /> },
  "platform-quotes": { label: "Quotes", icon: <IconQuote /> },
  "platform-renewals": { label: "Renewals", icon: <IconRenew /> },
  "platform-schools": { label: "Schools", icon: <IconSchool /> },
  "platform-groups": { label: "School groups", icon: <IconGroups /> },
  "platform-people": { label: "People", icon: <IconPeople /> },
  "platform-support": { label: "Support", icon: <IconSupport /> },
  "platform-subscriptions": { label: "Revenue", icon: <IconRevenue /> },
  "platform-games": { label: "Games", icon: <IconGames /> },
  "platform-devices": { label: "Devices", icon: <IconDevice /> },
  "platform-website": { label: "Website", icon: <IconWebsite /> },
  "platform-team": { label: "Team", icon: <IconTeam /> },
  "platform-audit": { label: "Activity log", icon: <IconActivity /> },
  "platform-system": { label: "Settings", icon: <IconSettings /> },
  "platform-children": { label: "Children", icon: <IconChildren /> },
  "platform-parents": { label: "Parents", icon: <IconParent /> },
  "platform-coupons": { label: "Coupons", icon: <IconCoupon /> },
  "platform-broadcast": { label: "Broadcast", icon: <IconBroadcast /> },
  "platform-operators": { label: "Operators", icon: <IconTeam /> },
  school: { label: "Home", icon: <IconOverview /> },
  "school-students": { label: "Children", icon: <IconChildren /> },
  "school-teachers": { label: "Teachers", icon: <IconTeacher /> },
  "school-classes": { label: "Classes", icon: <IconClasses /> },
  "school-reports": { label: "Reports", icon: <IconReports /> },
  "school-games": { label: "Game activity", icon: <IconGames /> },
  "school-try-games": { label: "Try games", icon: <IconPlay /> },
  "school-subscription": { label: "Billing", icon: <IconRevenue /> },
  "school-settings": { label: "Settings", icon: <IconSettings /> },
  "school-help": { label: "Help & support", icon: <IconHelp /> },
  teacher: { label: "Home", icon: <IconOverview /> },
  "teacher-play": { label: "Start play", icon: <IconPlay /> },
  "teacher-class": { label: "Class dashboard", icon: <IconAnalytics /> },
  "teacher-children": { label: "Children", icon: <IconChildren /> },
  "teacher-attendance": { label: "Attendance", icon: <IconAttendance /> },
  "teacher-reports": { label: "Reports", icon: <IconReports /> },
  "teacher-messages": { label: "Messages", icon: <IconSupport /> },
  "teacher-help": { label: "Help & support", icon: <IconHelp /> },
  parent: { label: "Home", icon: <IconOverview /> },
  "parent-reports": { label: "Progress", icon: <IconRevenue /> },
  "parent-attendance": { label: "Attendance", icon: <IconAttendance /> },
  "parent-messages": { label: "Messages", icon: <IconSupport /> },
  "parent-achievements": { label: "Achievements", icon: <IconAward /> },
  "parent-announcements": { label: "Announcements", icon: <IconBroadcast /> },
};

export const SETTINGS_CONTROL = {
  icon: <PrivacyIcon size={18} />,
  tint: "var(--color-indigo)",
} as const;

export function navLabel(id: RouteId, lang: Lang, fallback: string): string {
  return CONSOLE_NAV[id]?.label ?? NAV[id]?.label[lang] ?? fallback;
}

/**
 * What the chrome bar calls the screen a reader is on.
 *
 * 🔴 IT REPLACED A HARDCODED `Dashboard` IN `DashboardShell`. The shell printed
 * that one word above every adult screen in the product — Children, Teachers,
 * Classes, Our plan, Audit log — because the `title` PROP was removed (rightly:
 * it was a second `<h1>`) and the literal underneath it was not. A bar that
 * names the wrong page is worse than a bar that names none: the sidebar
 * highlights "Teachers", the heading says "Teachers", and the one line at the
 * top of the window insists it is the dashboard.
 *
 * ⚠️ IT IS THE SAME VOCABULARY AS THE SIDEBAR, BY CONSTRUCTION — `label` first,
 * `pageTitle` only where a tier's home needs a different word, `RouteDef.title`
 * as the floor. Deriving the chrome's word anywhere else is how the sidebar and
 * the bar above it end up calling one screen two things.
 */
export function pageTitleFor(id: RouteId, lang: Lang, fallback: string): string {
  return NAV[id]?.pageTitle?.[lang] ?? navLabel(id, lang, fallback);
}

export function navIcon(id: RouteId): ReactNode {
  return CONSOLE_NAV[id]?.icon ?? NAV[id]?.icon ?? null;
}

/**
 * ⚠️ FALLS BACK TO THE BRAND RATHER THAN TO NOTHING. A route added to the table
 * without a line here still gets a disc, in the one colour that is always
 * correct — a sidebar where five items have an identity colour and the sixth has
 * a hole is worse than one with none at all.
 */
export function navTint(id: RouteId): string {
  return NAV[id]?.tint ?? "var(--color-primary)";
}

/**
 * The two words under the wordmark.
 *
 * 🔴 IT IS THE ANSWER TO "WHOSE ACCOUNT AM I IN". An operator using X4 to act as
 * a head teacher sees the school tier's sidebar — same wordmark, same warm
 * cream, a list of nouns that read plausibly either way. `admin-panel.md` §8 is
 * written about exactly the mistake that follows: *"an operator who forgets they
 * are impersonating makes changes they attribute to the customer."* The banner
 * says an impersonation is running; this says which tier is on screen, always,
 * for everybody.
 */
/*
 * 🔴 `SUPER_ADMIN` WAS THE ONE ROLE MISSING, AND ITS ABSENCE IS WHAT PUT
 * "SUPER ADMIN" ON A PARENT'S SIDEBAR.
 *
 * The operator's label was not derived at all — `AppRouter`'s brand block
 * hardcoded the words under the wordmark. With this map silent for
 * `SUPER_ADMIN`, the operator tier read correctly by coincidence: the hardcoded
 * line was the only one drawn. Every OTHER tier drew both — the hardcoded
 * "SUPER ADMIN" and its own real label under it — so a parent was told they
 * were the platform operator, a teacher likewise.
 *
 * ⚠️ AND THAT INVERTS THE WHOLE POINT OF THIS FUNCTION, which the note above
 * states: it is *"the answer to 'whose account am I in'"*, written so an
 * operator acting as a head teacher cannot forget whose data they are changing
 * (`admin-panel.md` §8). A label that says SUPER ADMIN on every screen in the
 * product does not just fail to answer that question — it answers it wrongly
 * for everyone who is not an operator.
 */
/**
 * How a tier's sidebar is laid out — order and group headings — from the
 * console redesign (Figma "Kalqy", Components › Sidebar/*).
 *
 * ⚠️ LAYOUT, NOT PERMISSION. `navFor(role)` still decides which routes a role
 * may see; this only orders what it returned and draws the headings. A route a
 * layout does not mention is NOT dropped — it lands in a trailing untitled
 * section — so adding a route can never make it silently unreachable.
 */
export interface NavSectionDef {
  /** Heading above the section; the first section of a tier usually has none. */
  title?: Localized<string>;
  ids: readonly RouteId[];
}

const NAV_LAYOUT: Partial<Record<Role, readonly NavSectionDef[]>> = {
  SUPER_ADMIN: [
    { ids: ["platform", "platform-engagement"] },
    { title: { en: "Sales" }, ids: ["platform-requests", "platform-quotes", "platform-renewals"] },
    { title: { en: "Customers" }, ids: ["platform-schools", "platform-groups", "platform-people", "platform-support"] },
    { title: { en: "Money" }, ids: ["platform-subscriptions"] },
    { title: { en: "Product" }, ids: ["platform-games", "platform-devices", "platform-website"] },
    { title: { en: "Manage" }, ids: ["platform-audit", "platform-system"] },
  ],
  ADMIN: [
    { ids: ["school", "school-students", "school-teachers", "school-classes", "school-reports"] },
    { title: { en: "School" }, ids: ["school-try-games", "school-subscription", "school-settings", "school-help"] },
  ],
  TEACHER: [
    { ids: ["teacher", "teacher-play", "teacher-class", "teacher-children", "teacher-attendance"] },
    { title: { en: "Class" }, ids: ["teacher-reports", "teacher-messages", "teacher-help"] },
  ],
  PARENT: [
    {
      ids: [
        "parent",
        "parent-reports",
        "parent-attendance",
        "parent-messages",
        "parent-achievements",
        "parent-announcements",
      ],
    },
  ],
};
NAV_LAYOUT.PRINCIPAL = NAV_LAYOUT.ADMIN;
NAV_LAYOUT.COORDINATOR = NAV_LAYOUT.TEACHER;

/**
 * Routes the redesign folds into another screen rather than giving a sidebar
 * row: Children and Parents live under People, Coupons under Revenue,
 * Operators under Team, Broadcast under Settings, the game log under Reports.
 * Team itself is off the sidebar at the owner's request (2026-10-11) and is
 * reached at `#/platform/team`.
 * ⚠️ HIDDEN FROM THE SIDEBAR ONLY — the routes still resolve, and the screens
 * that absorb them link to them.
 */
const NAV_HIDDEN: ReadonlySet<RouteId> = new Set<RouteId>([
  "platform-children",
  "platform-parents",
  "platform-coupons",
  "platform-operators",
  "platform-team",
  "platform-broadcast",
  "school-games",
]);

export interface NavSection<T extends { id: RouteId }> {
  title: string | null;
  items: T[];
}

/** Group a role's visible routes into the sidebar's sections, in layout order. */
export function navSections<T extends { id: RouteId }>(
  role: Role | null,
  routes: readonly T[],
  lang: Lang,
): NavSection<T>[] {
  const layout = role ? NAV_LAYOUT[role] : undefined;
  if (!layout) return [{ title: null, items: [...routes] }];
  const byId = new Map(routes.map((r) => [r.id, r]));
  const used = new Set<RouteId>(NAV_HIDDEN);
  const sections: NavSection<T>[] = [];
  for (const def of layout) {
    const items = def.ids.flatMap((id) => {
      const r = byId.get(id);
      if (!r) return [];
      used.add(id);
      return [r];
    });
    if (items.length > 0) sections.push({ title: def.title?.[lang] ?? null, items });
  }
  const rest = routes.filter((r) => !used.has(r.id));
  if (rest.length > 0) sections.push({ title: null, items: rest });
  return sections;
}

const TIER: Partial<Record<Role, Localized<string>>> = {
  SUPER_ADMIN: { en: "Super admin" },
  ADMIN: { en: "School admin" },
  PRINCIPAL: { en: "Principal" },
  TEACHER: { en: "Teacher" },
  COORDINATOR: { en: "Coordinator" },
  PARENT: { en: "Parent" },
};

/**
 * 🔴 AN OPERATOR'S BADGE NAMES THEIR DUTY — "Sales", "Finance", "Viewer" — NOT
 * THE TIER. Every duty read "Super admin", which told a Viewer they could change
 * anything. "Super admin" stays only as the fallback while
 * `/auth/me/permissions` is in flight, or for an account with no duty on record.
 */
export function tierLabel(
  role: Role | null,
  lang: Lang,
  operatorRole: OperatorRole | null = null,
): string | null {
  if (role === null) return null;
  if (role === "SUPER_ADMIN" && operatorRole !== null) {
    return OPERATOR_ROLE_COPY[operatorRole].label;
  }
  return TIER[role]?.[lang] ?? null;
}

/**
 * The permission each operator sidebar row's screen needs for its MAIN READ —
 * the `@RequirePermissions` on that GET in the backend.
 *
 * 🔴 THE SIDEBAR GATED BY ROLE ONLY, SO EVERY DUTY SAW EVERY ROW. A Support
 * operator saw Revenue, opened it, and was told "Revenue could not be loaded" —
 * the server's 403, refusing correctly, dressed up as an outage.
 *
 * ⚠️ A COURTESY, NOT THE CONTROL. The server refuses the read either way, and
 * those screens show "your role can't see this" on a 403 for anyone who arrives
 * by URL. Writes inside a screen are gated there, not here.
 */
const NAV_PERMISSION: Partial<Record<RouteId, Permission>> = {
  platform: "platform.read", //               GET superadmin/insights/overview
  "platform-engagement": "platform.read", //  GET superadmin/insights/analytics
  "platform-requests": "platform.read", //    GET superadmin/console/pipeline
  "platform-quotes": "platform.read", //      GET superadmin/console/quotes
  "platform-renewals": "billing.read", //     GET superadmin/console/renewals
  "platform-schools": "platform.read", //     GET superadmin/insights/schools
  "platform-groups": "platform.read", //      GET superadmin/console/groups
  "platform-people": "platform.read", //      GET superadmin/insights/people/summary
  "platform-children": "platform.read",
  "platform-parents": "platform.read",
  "platform-support": "platform.read", //     GET superadmin/console/support
  "platform-subscriptions": "billing.read", // GET superadmin/console/revenue, …/invoices
  "platform-coupons": "billing.read", //      GET superadmin/coupons
  "platform-games": "platform.read",
  "platform-devices": "platform.read", //     GET superadmin/console/platform/devices
  "platform-website": "platform.read", //     GET superadmin/website (website.manage is for changes)
  "platform-team": "platform.read", //        GET superadmin/team (team.manage is for changes)
  "platform-operators": "platform.read",
  "platform-audit": "audit.read", //          GET audit
  "platform-system": "platform.read", //      GET superadmin/console/billing-profile
  "platform-broadcast": "platform.read",
};

/**
 * ⚠️ EVERY OPERATOR DUTY HOLDS `platform.read` (`OPERATOR_PERMISSIONS` in the
 * backend), so a row that needs only it is shown at once rather than held back
 * while the permissions load — an empty sidebar for a beat on every sign-in
 * would be worse than the bug. Only the rows a duty may lack wait to be known.
 */
const EVERY_DUTY: Permission = "platform.read";

/** Whether a sidebar row's screen can load for this person. Routes with no entry are ungated. */
export function navPermitted(id: RouteId, can: (p: Permission) => boolean): boolean {
  const need = NAV_PERMISSION[id];
  if (need === undefined || need === EVERY_DUTY) return true;
  return can(need);
}

/**
 * The count pills on sidebar rows — what is waiting for this person.
 *
 * ⚠️ A COURTESY, NOT A WORK QUEUE. `useNavBadges` reads `GET /me/nav-badges`
 * (one request for every key, polled while the tab is visible) and swallows
 * every error: a missing pill must never look like a broken sidebar. The
 * server decides each count with the same scoping as the screen behind it, so
 * a key a person cannot see simply never comes back.
 */
export type NavBadgeKey = "requests" | "renewals" | "support" | "messages";

/** Which sidebar row draws which count. A row with no entry never shows a pill. */
export const NAV_BADGE: Partial<Record<RouteId, NavBadgeKey>> = {
  "platform-requests": "requests",
  "platform-renewals": "renewals",
  "platform-support": "support",
  "teacher-messages": "messages",
  "parent-messages": "messages",
};
